import React, { useState } from "react";
import {
  View,
  TextInput,
  FlatList,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { useDebounce } from "use-debounce";

import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import FormLibrary from "../CoachLibrary/FormLibrary";
import BlockLibrary from "../CoachLibrary/BlockLibrary";
import Colors from "../../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useDevice, useScreenDimensions } from "../../_layout";
import CoachFoodLibrary from "./CoachFoodLibrary";
import CoachNutritionPlanLibrary from "./CoachNutritionPlanLibrary";
import LogoImage from "../../Components/LogoImage";
import SyncStatusIndicator from "../../Components/SyncStatusIndicator";
import Fonts from "../../../Resources/Fonts";
import { SyncStatus } from "../../../Resources/Constants";

const OFF_SEARCH_URL = "https://world.openfoodfacts.org/cgi/search.pl";
const PAGE_SIZE = 20;

// function useFoodSearch(searchTerm) {
//   return useInfiniteQuery({
//     queryKey: ["foodSearch", searchTerm],
//     queryFn: ({ pageParam = 1 }) =>
//       axios
//         .get(OFF_SEARCH_URL, {
//           params: {
//             search_terms: searchTerm,
//             search_simple: 1,
//             json: 1,
//             page: pageParam,
//             page_size: 20,
//           },
//         })
//         .then((res) => res.data.products),
//     enabled: Boolean(searchTerm),
//     getNextPageParam: (lastPage, pages) =>
//       lastPage.length === 20 ? pages.length + 1 : undefined,
//   });
// }
function useFoodSearch(searchTerm) {
  return useInfiniteQuery({
    queryKey: ["foodSearch", searchTerm],
    queryFn: ({ pageParam = 0 }) =>
      axios
        .get(OFF_SEARCH_URL, {
          params: {
            search_terms: searchTerm,
            search_simple: 1,
            json: 1,
            page: pageParam + 1, // OFF is 1-indexed
            page_size: PAGE_SIZE,
          },
        })
        .then((res) => res.data.products),
    enabled: Boolean(searchTerm),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    select(data) {
      const all = data.pages.flat();
      const clean = all.filter(
        (item) =>
          item.product_name &&
          item.brands?.trim() &&
          item.nutriments?.["energy-kcal_value"] != null &&
          item.nutriments?.carbohydrates_value != null &&
          item.nutriments?.proteins_value != null &&
          item.nutriments?.fat_value != null
      );
      return {
        pages: [clean],
        pageParams: data.pageParams,
      };
    },
  });
}

function rankFoods(list, term) {
  const t = term.trim().toLowerCase();

  function namesOf(item) {
    return [
      item.product_name_es,
      item.generic_name_es,
      item.product_name,
      item.generic_name,
    ]
      .filter(Boolean)
      .map((s) => s.toLowerCase());
  }

  return (
    list
      .map((item) => {
        let score = 0;
        const names = namesOf(item);

        // 1) exact
        if (names.some((n) => n === t)) score += 100;
        // 2) prefix
        else if (names.some((n) => n.startsWith(t))) score += 50;
        // 3) substring
        else if (names.some((n) => n.includes(t))) score += 10;

        // 4) Hacendado brand boost
        if (
          item.brands
            .toLowerCase()
            .split(/[,;]/) // allow “brand1, brand2”
            .map((b) => b.trim())
            .includes("hacendado")
        ) {
          score += 20;
        }

        return { item, score };
      })
      // only keep hits
      .filter(({ score }) => score > 0)
      // highest first
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item)
  );
}

const CoachNutritionProva = ({ coach }) => {
  const [term, setTerm] = useState("");
  const [debouncedTerm] = useDebounce(term, 1000);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFoodSearch(debouncedTerm);

  // when searchTerm is empty, show just the input
  if (!debouncedTerm) {
    return (
      <View style={{ flex: 1, padding: 16 }}>
        <TextInput
          placeholder="Search foods…"
          value={term}
          onChangeText={setTerm}
          style={{
            padding: 8,
            borderBottomWidth: 1,
            borderColor: "#ccc",
          }}
        />
      </View>
    );
  }

  if (isLoading) {
    return <ActivityIndicator style={{ flex: 1, marginTop: 20 }} />;
  }
  if (isError) {
    return <Text style={{ padding: 20 }}>Oops, something went wrong.</Text>;
  }

  // <-- guard here
  //   const items = data?.pages?.flat() ?? [];
  const items = data.pages.flat();
  const filteredAndRanked = rankFoods(items, debouncedTerm);

  const ProductCard = ({ item }) => {
    const name = item.product_name || item.generic_name || "—";

    // List out the macros you care about, with the OFF key and the unit you want
    const macros = [
      { key: "energy-kcal_value", label: "Energy", unit: "kcal" },
      { key: "carbohydrates_value", label: "Carbs", unit: "g" },
      { key: "proteins_value", label: "Protein", unit: "g" },
      { key: "fat_value", label: "Fat", unit: "g" },
      { key: "sugars_value", label: "Sugars", unit: "g" },
    ];

    return (
      <View style={{ padding: 12, borderBottomWidth: 1, borderColor: "#eee" }}>
        {/* Product name */}
        <Text
          style={{ fontSize: 16, fontWeight: "600" }}
          onPress={() => console.log("ITEM: ", item)}
        >
          {name}
        </Text>
        <Text style={{ fontSize: 12, color: "#666" }}>
          {item.brands || "Unknown brand"}
        </Text>
        {/* Macros row */}
        <View
          style={{
            flexDirection: "row",
            marginTop: 8,
            justifyContent: "space-between",
          }}
        >
          {macros.map(({ key, label, unit }) => {
            const value = item.nutriments?.[key];
            return (
              <View
                key={key}
                style={{ alignItems: "center", minWidth: 50, marginRight: 12 }}
              >
                <Text style={{ fontSize: 10, color: "#666" }}>{label}</Text>
                <Text style={{ fontSize: 14, fontWeight: "500" }}>
                  {value != null ? `${value}${unit}` : "—"}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <TextInput
        placeholder="Search foods…"
        value={term}
        onChangeText={setTerm}
        style={{
          padding: 8,
          borderBottomWidth: 1,
          borderColor: "#ccc",
        }}
      />

      <FlatList
        data={filteredAndRanked}
        keyExtractor={(item) => item.code}
        renderItem={({ item }) => <ProductCard item={item} />}
        ListFooterComponent={() =>
          hasNextPage ? (
            <TouchableOpacity
              onPress={() => fetchNextPage()}
              style={{
                padding: 12,
                margin: 16,
                backgroundColor: "#eee",
                borderRadius: 4,
                alignItems: "center",
              }}
            >
              {isFetchingNextPage ? (
                <ActivityIndicator />
              ) : (
                <Text>Load more...</Text>
              )}
            </TouchableOpacity>
          ) : null
        }
      />
    </View>
  );
};

const CoachNutrition = ({ coach }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const [selectedTab, setSelectedTab] = useState("foods-string");
  const TabOptions = ["foods-string", "nutritionPlans-string"];

  const components = {
    "foods-string": CoachFoodLibrary,
    "nutritionPlans-string": CoachNutritionPlanLibrary,
  };

  const ComponentToShow = components[selectedTab] || null;

  const TabBar = () => {
    const OptionButton = ({ option }) => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={() => setSelectedTab(option)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View style={{ alignItems: "center" }}>
            <Text
              style={[
                styles.tabText,
                {
                  color: "black",
                },
              ]}
            >
              {t(option)}
            </Text>
            <View
              style={[
                styles.underline,
                {
                  backgroundColor:
                    selectedTab === option
                      ? Colors.bubbleColor
                      : isHovered
                      ? "rgba(63, 123, 141, 0.65)"
                      : "transparent",
                },
              ]}
            />
          </View>
        </TouchableOpacity>
      );
    };
    return (
      <>
        <View style={{ flexDirection: "row", width: 700 }}>
          {TabOptions.map((option, index) => (
            <View
              style={{
                flexDirection: "row",
                marginRight: 30,
              }}
              key={index}
            >
              <OptionButton option={option} />
              <View style={{ flex: 1 }} />
            </View>
          ))}
        </View>
        <View
          style={{
            width: "100%",
            height: 0.6,
            backgroundColor: Colors.borderColor,
          }}
        />
      </>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          marginBottom: 24,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={[
              Fonts.bold24,
              { marginRight: screenSize === "small" ? 8 : 12 },
            ]}
          >
            {t("nutrition-string")}
          </Text>
          {coach.syncStatus !== SyncStatus.SYNCED && (
            <SyncStatusIndicator
              syncStatusManager={coach.syncStatusManager}
              onPress={() => coach.startOrResetBatchTimer()}
            />
          )}
        </View>

        <LogoImage coach={coach} />
      </View>

      <TabBar />
      {ComponentToShow && <ComponentToShow coach={coach} />}
    </View>
  );
};

const styles = StyleSheet.create({
  tabText: {
    fontSize: 16,
    fontWeight: "500",
  },
  underline: {
    height: 2,
    width: "100%",
    alignSelf: "center",
    marginTop: 5,
  },
});

export default CoachNutrition;
